import type { TracePointKind, Workflow, WorkflowTraceHandle } from '@toolproof-core/genesis/_contract/_index';

import type { RunGraphResult as BuilderRunGraphResult } from '@/workflow-builder/_contract/runWorkflow';

const SUPPORTED_TRACE_POINT_KINDS = new Set<TracePointKind>([
    'workflow_start',
    'tick',
    'suspended',
    'workflow_end',
]);

type InterruptPayload = {
    langgraphThreadId: string;
    context: Record<string, unknown>;
};

export type RunGraphRequest = {
    workflow: Workflow;
    useLocalLanggraph?: boolean;
    writeLocalWorkflowTrace?: boolean;
};

export type RunGraphStreamCallbacks = {
    onTracePoint?: (tracePoint: unknown) => void;
    onSuspended?: (payload: InterruptPayload) => void;
    onParseError?: (error: unknown, line: string) => void;
};

export type RunGraphResult = BuilderRunGraphResult;

function asRecord(value: unknown): Record<string, unknown> | null {
    if (typeof value !== 'object' || value === null) return null;
    return value as Record<string, unknown>;
}

function getTracePointKind(rawTracePoint: unknown): TracePointKind | null {
    const rec = asRecord(rawTracePoint);
    if (!rec) return null;

    const tracePointKind = rec.tracePointKind;
    if (typeof tracePointKind !== 'string') return null;

    return tracePointKind as TracePointKind;
}

function extractInterruptPayload(tracePoint: unknown): InterruptPayload | null {
    const rec = asRecord(tracePoint);
    if (!rec) return null;

    const langgraphThreadId = rec.langgraphThreadId;
    if (typeof langgraphThreadId !== 'string' || langgraphThreadId.length === 0) {
        return null;
    }

    const interruptData = asRecord(rec.interruptData);
    if (interruptData) {
        return {
            langgraphThreadId,
            context: interruptData,
        };
    }

    return {
        langgraphThreadId,
        context: rec,
    };
}

async function consumeNdjsonStream(opts: {
    body: ReadableStream<Uint8Array>;
    onRawObject: (value: unknown) => void;
    onParseError?: (error: unknown, line: string) => void;
}) {
    const reader = opts.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const flushLine = (line: string) => {
        const trimmed = line.trim();
        if (!trimmed) return;

        try {
            const parsed = JSON.parse(trimmed);
            opts.onRawObject(parsed);
        } catch (error) {
            opts.onParseError?.(error, trimmed);
        }
    };

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let splitIndex = buffer.indexOf('\n');
        while (splitIndex >= 0) {
            const line = buffer.slice(0, splitIndex);
            buffer = buffer.slice(splitIndex + 1);
            flushLine(line);
            splitIndex = buffer.indexOf('\n');
        }
    }

    const trailing = decoder.decode();
    if (trailing) {
        buffer += trailing;
    }

    if (buffer.trim()) {
        flushLine(buffer);
    }
}

function forwardGraphEvent(tracePoint: unknown): void {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('toolproof:graphEvent', { detail: tracePoint }));
}

export async function runGraphAndForwardTrace(args: {
    request: RunGraphRequest;
    callbacks?: RunGraphStreamCallbacks;
    signal?: AbortSignal;
}): Promise<RunGraphResult> {
    const { request, callbacks, signal } = args;

    let response: Response;
    try {
        response = await fetch('/api/run-graph', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
            signal,
        });
    } catch (error) {
        return {
            ok: false,
            status: 0,
            error: error instanceof Error ? error.message : 'Failed to call /api/run-graph.',
        };
    }

    if (!response.ok) {
        let errorText = `Run graph failed with status ${response.status}.`;
        try {
            const responseText = await response.text();
            if (responseText.trim().length > 0) {
                errorText = responseText;
            }
        } catch {
            // Keep fallback error text.
        }

        return {
            ok: false,
            status: response.status,
            error: errorText,
        };
    }

    if (!response.body) {
        return {
            ok: false,
            status: response.status,
            error: 'Run graph response body was empty.',
        };
    }

    let workflowTraceHandle: WorkflowTraceHandle | undefined;
    let streamError: string | undefined;

    await consumeNdjsonStream({
        body: response.body,
        onRawObject: (rawTracePoint) => {
            const record = asRecord(rawTracePoint);
            if (record?.streamEventKind === 'workflow-trace-persisted') {
                if (typeof record.workflowTraceHandle === 'string') {
                    workflowTraceHandle = record.workflowTraceHandle as WorkflowTraceHandle;
                }
                return;
            }
            if (record?.streamEventKind === 'workflow-trace-persistence-failed') {
                streamError = typeof record.error === 'string'
                    ? record.error
                    : 'WorkflowTrace persistence failed.';
                return;
            }

            const tracePointKind = getTracePointKind(rawTracePoint);
            if (!tracePointKind || !SUPPORTED_TRACE_POINT_KINDS.has(tracePointKind)) {
                return;
            }

            if (tracePointKind === 'suspended') {
                const payload = extractInterruptPayload(rawTracePoint);
                if (payload) {
                    callbacks?.onSuspended?.(payload);
                }
            }

            callbacks?.onTracePoint?.(rawTracePoint);
            forwardGraphEvent(rawTracePoint);
        },
        onParseError: callbacks?.onParseError,
    });

    if (streamError !== undefined) {
        return {
            ok: false,
            status: response.status,
            error: streamError,
        };
    }

    if (workflowTraceHandle === undefined) {
        return {
            ok: false,
            status: response.status,
            error: 'Run graph stream ended before WorkflowTrace persistence was acknowledged.',
        };
    }

    return {
        ok: true,
        status: response.status,
        workflowTraceHandle,
    };
}
